import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ds2'
import { LocationSearch } from 'components/LocationSearch/LocationSearch'
import type { CityLocationData } from 'utils/location-search/LocationDataSearchV2'
import { cn } from 'utils/cn'
import { useAnchoredPopover } from '../hooks/useAnchoredPopover'
import { DriverLastLocationDto, updateBoxTruckDriverLocation } from '../services/boxTruckLoadsApi'

interface DriverLocationEditorProps {
    /** The driver whose location the operator is correcting. */
    userId: string
    /** The stored fix after a successful save — the caller patches its row/list state with it. */
    onSaved: (location: DriverLastLocationDto) => void | Promise<void>
    /** Optional notification for a parent surface that highlights the row being edited. */
    onOpenChange?: (open: boolean) => void
    /** Compact chevron for tight table cells (default); false renders the standard 16px control icon. */
    small?: boolean
}

/**
 * Operator-corrected driver location — the chevron-next-to-the-locality pattern (Atrek's dispatch board).
 * Opens an anchored popover with the SAME city/ZIP search the route-map editor uses to add points
 * (LocationSearch — local city dataset + live ZIP geocode), plus an Update button. Saving writes both the
 * driver_locations fix and the driver's profile locality server-side, so My Drivers, the load drawer's
 * Nearby Drivers, and the map all reflect it.
 */
export const DriverLocationEditor = ({ userId, onSaved, onOpenChange, small = true }: DriverLocationEditorProps) => {
    const { open, anchorRect, containerRef, triggerRef, panelRef, close, togglePanel, panelPositionStyle } = useAnchoredPopover()
    const [pending, setPending] = useState<CityLocationData | null>(null)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(false)

    useEffect(() => onOpenChange?.(open), [open, onOpenChange])

    // LocationSearch's displayed value is driven by `defaultLocation` (its sync effect re-runs on every
    // render because the prop defaults to a fresh `{}` — and resets the value to null when empty). So the
    // pick must be FED BACK as defaultLocation or the re-render caused by setPending immediately clears the
    // input — the "selected option doesn't stick" bug. Same pattern as CreateEditRadarModal.
    const pendingAsDefault = useMemo(
        () =>
            pending
                ? {
                      city: pending.city,
                      stateProv: pending.stateCode,
                      postalCode: pending.postalCode ?? '',
                      latitude: pending.latitude ?? undefined,
                      longitude: pending.longitude ?? undefined,
                  }
                : undefined,
        [pending]
    )

    const save = async () => {
        if (!pending || pending.latitude == null || pending.longitude == null || saving) return
        setSaving(true)
        setError(false)
        try {
            const stored = await updateBoxTruckDriverLocation(userId, {
                lat: pending.latitude,
                lng: pending.longitude,
                city: pending.city || undefined,
                state: pending.stateCode || undefined,
                postalCode: pending.postalCode,
            })
            await onSaved(stored)
            setPending(null)
            close()
        } catch (err) {
            console.error('[box-truck] driver location update failed', err)
            setError(true)
        } finally {
            setSaving(false)
        }
    }

    return (
        <div ref={containerRef} className="inline-flex" onClick={(e) => e.stopPropagation()}>
            <button
                ref={triggerRef}
                type="button"
                onClick={togglePanel}
                aria-label="Edit driver location"
                title="Edit driver location"
                className={cn(
                    'inline-flex cursor-pointer items-center justify-center rounded border-none bg-transparent text-foreground-muted transition-colors hover:bg-default hover:text-foreground',
                    small ? 'h-5 w-5' : 'h-7 w-7'
                )}
            >
                <ChevronDown size={small ? 12 : 16} className={cn('transition-transform duration-200', open ? 'rotate-180' : 'rotate-0')} />
            </button>
            {open &&
                anchorRect &&
                createPortal(
                    <div
                        ref={panelRef}
                        // The trigger is a tiny chevron, so ignore the hook's trigger-width sizing.
                        style={{ ...panelPositionStyle, width: 300 }}
                        className="flex flex-col gap-2 rounded-2xl bg-surface p-3 shadow-overlay"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">Location</span>
                            <Button variant="primary" size="sm" loading={saving} disabled={pending?.latitude == null} onClick={() => void save()}>
                                Update
                            </Button>
                        </div>
                        {/* Same input the route-map editor's add-point search uses: local city dataset + live
                            5-digit-ZIP geocode; a pick carries coordinates. `insideDialog` renders the option
                            list INLINE (no MUI portal) — portaled options land outside panelRef, so the anchored
                            popover's outside-click close fired on the very click that picked an option. */}
                        <LocationSearch
                            placeholder="City or ZIP code…"
                            onLocationSelect={(loc) => setPending(loc)}
                            defaultLocation={pendingAsDefault}
                            insideDialog
                        />
                        {error && <span className="text-xs text-danger">Couldn’t update the location — try again.</span>}
                    </div>,
                    document.body
                )}
        </div>
    )
}
